import { LanguageMetadata } from "../types";

export const en = {
  translation: {
    header: {
      prices: "Prices",
      courses: "Courses",
      about: "About",
      testimonials: "Testimonials",
      contacts: "Contacts",
      home: "Home",
      switchLanguage: "Switch language",
      closeMenu: "Close menu",
    },
    footer: {
      contact_us: "Contact Us",
      working_hours: "Working Hours",
      contacts: "Contacts",
      partners: "Partners",
      get_in_touch: "Get in Touch",
      follow_us: "Follow Us",
      navTitle: "Menu",
      privacyPolicy: "Privacy policy",
      cookiePolicy: "Cookie policy",
      copyright: "© All rights reserved 2026",
    },
    feedback: {
      not_found: {
        title: "Page not found",
        description: "Sorry, we couldn&apos;t find the page you&apos;re looking for.",
        go_back: "Go back",
      },
    },
    cookieConsent: {
      title: "Cookies",
      acceptButton: "Accept",
      declineButton: "Decline",
      messageLead: "This website uses cookies and analytics to improve your experience. See our",
      messageTrail: "for more information.",
      privacyPolicyLink: "privacy policy",
    },
    legal: {
      privacy: {
        title: "Privacy Policy",
        intro:
          "We value your privacy and are committed to being transparent about the data we collect and how it is used. This website uses analytics to monitor website performance and improve user experience. This analytics service collects anonymous usage data through cookies to help us understand how visitors interact with our website.",
        anonymousIntro: "The data collected by our analytical tool is anonymous and includes:",
        list: {
          pageViews: "Page views and navigation patterns",
          device: "Device type and browser information",
          geo: "Geographic location (country level only)",
          performance: "Website performance metrics",
        },
        noPersonal:
          "We do not collect or store any personal information through our analytics. You can choose to disable cookies through your browser settings, although this may affect some website functionality.",
        moreInfoPrefix: "For more information about privacy practices, please visit",
        vercelPolicyLinkLabel: "Privacy Policy",
      },
      cookies: {
        title: "Cookie policy",
        intro:
          "We use cookies and similar technologies where necessary for the website to function and to measure anonymous traffic (for example through analytics). When you first visit the site, you can accept or decline non-essential cookies via the banner. You can also control cookies through your browser settings.",
      },
    },
    contact: {
      contactTitle: "Contact us",
      contactSubtitle: "Please provide your details and we will get back to you shortly.",
      infoTitle: "Get in touch",
      infoDescription:
        "Whether you are booking a dive, asking about courses, or planning a visit - send us a message and we will respond as soon as we can.",
      phoneLabel: "Phone",
      emailLabel: "Email",
      locationLabel: "Location",
      locationValue: "Arapya Beach, Tsarevo, Burgas 8260, Bulgaria",
      fullName: "Full name",
      email: "Email",
      phoneNumber: "Phone number",
      subject: "Subject",
      subjectPlaceholder: "Choose a topic",
      subjectGeneral: "General inquiry",
      subjectBooking: "Booking & dives",
      subjectCourses: "Courses & certification",
      subjectOther: "Other",
      message: "Message",
      messagePlaceholder: "Write your message here...",
      agreeToPolicies: "By selecting this, you agree to our <link/>",
      privacyPolicyLink: "privacy policy",
      privacyPolicyNote: "By submitting the form, you agree to our <link>privacy policy</link>.",
      submitButton: "Send message",
      send: "Send",
      sendMessage: "Send us a message",
      alertSuccess: "✅ Message sent successfully!",
      alertError: "❌ Error sending! Please try again.",
      configurationError:
        "The contact form is not configured right now. Please call or email us using the details on this page.",
    },
    contacts: {
      parallax: {
        title: "Get in Touch",
        subtitle: "We're here to answer all your questions",
        imageAlt: "Contacts page header background",
      },
    },
    prices: {
      columns: {
        activity: "Activity",
        course: "Course",
        price: "Price",
      },
      tourist: {
        title: "One Day Activities",
        tryDive: "Try dive (including speed boat, instructor, full equipment)",
        snorkeling: "Snorkeling (including speed boat, instructor, full equipment)",
        passenger: "Passenger",
      },
      certified: {
        title: "Certified Divers",
        boatDive: "1 boat dive (including speed boat, tanks, weights, guide)",
        twoDives: "1 day/2 boat dives (including speed boat, tanks, weights, guide)",
        nightDive: "Night dive (including speed boat, tanks, weights, guide)",
        mopangWreck: "Wreck diving to Mopang",
        rodinaWreck: "Wreck diving to Rodina",
        equipmentRental: "1 day full equipment rental",
      },
      cmas: {
        title: "Courses",
        bubbleMaker: "CMAS Bubblemaker for children 8-12 years",
        sealTeam: "CMAS Sealteam for children in a swimming pool",
        scubaDiver: "CMAS Scuba Diver (2/3 days)",
      },
    },
    home: {
      hero: {
        eyebrow: "Scuba Diving Center Arapya · Black Sea",
        title: "Dive into your next",
        titleHighlight: "adventure",
        subtitle:
          "Experience the wonders of the underwater world with expert diving tours and courses",
        scroll: "Scroll",
      },
      services: {
        sectionEyebrow: "Programs & experiences",
        title: "Our Offerings",
        subtitle:
          "Try-dives, boat dives, and CMAS courses - choose what fits your level and book with our team.",
        whatsIncluded: "What's included:",
        learnMore: "Learn more",
        level: {
          beginner: "Beginner",
          intermediate: "Intermediate",
          recommended: "Recommended",
          advanced: "Advanced",
        },
      },
      mission: {
        title: "Our Mission",
        description:
          "To inspire love and respect for life in the Black Sea, revealing its exceptional beauty and vulnerability. Through each dive, we not only offer an unforgettable adventure, but also raise awareness of the need to preserve this unique marine world for future generations.",
      },
      testimonials: {
        sectionEyebrow: "Guest reviews",
        title: "What our clients say",
        subtitle: "About their diving experience with us",
      },
      faq: {
        title: "Frequently Asked Questions",
        questions: {
          when: {
            q: "When do the dives take place?",
            a: "We officially open on June 1st and operate until September 20th. Dives are conducted under suitable conditions and with advance booking. The exact schedule is confirmed upon registration!",
          },
          experience: {
            q: "Do I need experience to dive?",
            a: "No, the experience is suitable for complete beginners as well as more experienced divers. The instructor will provide everything you need and guide you through the entire process.",
          },
          whatToBring: {
            q: "What should I bring with me?",
            a: "All you need is usual beach wear – swimsuit, flip-flops, towel and sunscreen. Everything else, including diving equipment, will be provided.",
          },
          restrictions: {
            q: "Are there age and health restrictions?",
            a: "The minimum age for diving is 10 years, and for persons under 18, parental/guardian consent is required. Diving is not suitable for certain medical conditions such as inflamed sinuses, runny nose, eardrum problems, high blood pressure and others.",
          },
          friends: {
            q: "Can I bring friends?",
            a: "Yes! You can share the experience with a friend or partner, with a maximum of two participants per dive.",
          },
        },
      },
      newsletter: {
        title: "Subscribe",
        subtitle:
          "We respect your privacy. Receive current news and interesting articles directly in your inbox.",
        emailPlaceholder: "Enter your email",
        subscribeButton: "Subscribe",
        subscribeSuccess: "Thanks — you're on the list.",
        subscribeError: "Something went wrong. Please try again in a moment.",
        weeklyArticles: "Weekly articles",
        weeklyArticlesDesc:
          "Every week you will receive useful and interesting articles directly to your email.",
        noSpam: "No spam",
        noSpamDesc:
          "We respect your privacy. Receive current news and interesting articles directly in your inbox.",
      },
    },
    about: {
      hero: {
        title: "Diving, teaching and community since 1999",
        subtitle:
          "Our base at Arapya Beach brings together BNAU-certified instructors, well-maintained gear, and boat access to standout Black Sea dive sites.",
        bannerAlt: "Scuba Diving Center Arapya - dive base and coastline at Arapya Beach",
      },
      timeline: {
        1999: "Beginning in Kiten under the name Black Sea Scuba Diving School",
        2001: "Moving to Arapya camping and birth of the current name",
        2004: "Official founding and certification by BNAU",
        today: "Today",
        todayDesc: "Modern base, motorboat and over 25 years of experience",
      },
      certification: {
        title: "Certification and Professionalism",
        bnau: {
          title: "BNAU Certified",
          desc: "Officially recognized instructors",
        },
        modern: {
          title: "Modern Methods",
          desc: "Quality training by modern standards",
        },
        equipment: {
          title: "Modern Equipment",
          desc: "Current and safe equipment",
        },
        experience: {
          title: "25+ Years of Experience",
          desc: "Trust and professionalism since 1999",
        },
        findUs: "You can find us among the registered instructors",
        here: "here",
      },
      facilities: {
        sectionTitle: "Our Facilities",
        base: {
          title: "Base in Arapya Camping",
          desc: "We have our own base, including an equipped training hall, preparation pool, air compressor and hotel part. The center is located between Tsarevo and Lozenets - the perfect place for training and adventure.",
          imageAlt: "Dive center base at Arapya campsite",
        },
        boat: {
          title: "Motorboat with Capacity for 8 Divers",
          desc: "We have a motorboat and full equipment, which allows us flexibility and variety when choosing diving locations. Our team of qualified instructors guarantees professionalism and safety.",
          imageAlt: "The dive center motorboat",
        },
      },
      gallery: {
        title: "Photo Gallery",
        centerPhotoAlt: "Scuba Arapya dive center",
      },
    },
    services: {
      courses: {
        title: "Diving Courses",
        shortDescription: "Learn to dive with CMAS and PADI certified instructors.",
        price: "500 €",
        features: {
          cmasCourse: "CMAS certified beginner course",
          allLevels: "Courses for all levels",
          specialized: "Specialized courses",
          specializedDesc: "Including night diving, underwater photography and more",
        },
        content: {
          p1: "Imagine breathing underwater, weightless, as vibrant marine life drifts past and sunlight filters through the depths. That's not a dream—it's what awaits you just one week from now. Our CMAS and PADI certified instructors have guided hundreds of first-timers through that magical moment when fear transforms into wonder.",
          p2: "Over seven days, you'll master everything from your first breath through a regulator to confidently navigating underwater terrain. We blend classroom theory with hands-on pool sessions before taking you into the open water. Every piece of equipment, every lesson, every safety protocol is designed around one goal: giving you the skills and confidence to explore safely, wherever your curiosity leads.",
          p3: "Your internationally recognized CMAS certificate isn't just a card—it's a passport to dive sites across the globe, from tropical reefs to historic wrecks. You'll join a worldwide community of divers who've discovered that the ocean isn't a place to fear, but a world to explore.",
          p4: "Ready to take the plunge? Contact us today to reserve your spot and receive detailed information about upcoming course dates, pricing, and what to bring. We're here to answer any questions and help you start your underwater journey. The ocean is waiting—let's dive in together.",
        },
      },
      dayDive: {
        title: "Day Dive",
        shortDescription: "Start your adventure with a one-day underwater tour!",
        price: "60 €",
        features: {
          guides: "Experienced dive guides",
          equipment: "Tanks, weights and equipment",
        },
        content: {
          p1: "Start your adventure with a one-day underwater tour! This is the perfect opportunity for complete beginners to learn the basics of diving.",
          p2: "It takes about two and a half hours and is specially prepared for absolute beginners. No prior experience needed! We will show you the breathing techniques and basic underwater movements.",
          p3: "During the day dive you will get in touch with the underwater world, under the guidance of our experienced instructors, who will provide you with safety and confidence throughout.",
          p4: "Diving is a wonderful way to immerse yourself in a new hobby and discover the magic of the underwater world. Call us to reserve your spot and start your dive today!",
        },
      },
      boatDive: {
        title: "Boat Dive",
        shortDescription: "Diving outside the bay, for certified divers only.",
        price: "100 €",
        features: {
          wreckDive: "Boat dive to shipwreck",
          certifiedOnly: "For certified divers only",
        },
        content: {
          p1: "Experience an exciting dive to shipwrecks, exclusively for certified divers. Our boat will take you to some of the most interesting underwater sites in the area.",
          p2: "Dive with our CMAS certified instructors as the location can be discussed in advance. A minimum of two divers are required for the boat dive.",
          p3: "Boat diving gives you a unique opportunity to explore underwater wonders at depth, accessible only by swimming. From sunken ships to underwater rocks, each location has its own exciting experience.",
          p4: "Expect safe and professional diving in the most beautiful underwater locations in the region. Please contact us to reserve your spot and learn more about upcoming dates and prices.",
        },
      },
      advancedCourse: {
        title: "Advanced",
        shortDescription: "Technical course for advanced divers.",
        price: "500 €",
        features: {
          certifiedOnly: "For certified divers only",
          navigation: "Underwater navigation skills",
          navigationDesc: "Includes compass navigation and natural landmarks",
        },
        content: {
          p1: "Expand your diving skills with our advanced course! This course is designed for certified divers who want to improve their techniques and learn new underwater strategies.",
          p2: "With the advanced course you will learn techniques for diving in various conditions, such as deep dives and dives in currents. You will also learn how to use more specialized equipment.",
          p3: "You will have the opportunity to perform more complex maneuvers and become familiar with emergency procedures that will prepare you for any situations you may encounter underwater.",
          p4: "The course will be conducted in various underwater locations that will provide you with new challenges and opportunities to demonstrate your advanced skills.",
        },
      },
    },
    courses: {
      seo: {
        title: "CMAS Diving Courses - Scuba Diving Center Arapya",
        description:
          "Professional CMAS diving courses at Arapya Beach. From beginner to advanced levels with BNAU-certified instructors. Get internationally recognized certification.",
      },
      hero: {
        title: "CMAS Diving Courses",
        subtitle:
          "Professional training with internationally recognized certification for all levels",
        bannerAlt: "CMAS diving courses at Scuba Diving Center Arapya",
      },
    },
    seo: {
      defaultTitle: "Scuba Diving Center Arapya",
      defaultDescription:
        "Black sea scuba diving in Bulgaria. Open Water Diver courses, SSI certification, wreck diving & jet ski near Arapya and Pomorie. Contact us: +359 898 839 357",
      defaultKeywords:
        "scuba diving bulgaria, black sea scuba diving Bulgaria, best places to scuba dive, скуба дайвинг, водолазно гмуркане, гмуркане в България, подводни спортове, Black sea diving, wreck diving, diving courses bulgaria, open water diver курс, SSI, Arapya, Pomorie, swimming, jet ski, diving regulator",
      aboutTitle: "About us - Scuba Diving Center Arapya",
      aboutDescription:
        "Learn about our dive center at Arapya Beach: BNAU-certified instructors, training facilities, motorboat dives, and over 25 years on the Bulgarian Black Sea coast.",
    },
    testimonials: {
      data: {
        1: {
          name: "Gabriela Dimitrova",
          handle: "@gabi",
          content:
            "The experience with Diving Center Arapya was unforgettable! Exceptional professionals who make you feel confident and calm throughout.",
          date: "10:12 AM · Aug 19, 2024",
        },
        2: {
          name: "Antonia Angelova",
          handle: "@toniq",
          content:
            "Unique experience! I highly recommend it! The instructor was great, with a sense of humor and explained everything very carefully.",
          date: "3:25 PM · Jul 11, 2024",
        },
        3: {
          name: "Alexander Ganchev",
          handle: "@sasho_first",
          content:
            "It was my first time and I received special attention. Instructor Anastasia was extremely patient and caring. I recommend!",
          date: "11:47 AM · Sep 2, 2024",
        },
        4: {
          name: "Kalina Evtimova",
          handle: "@kalina",
          content:
            "Great experience! My friend and I dived for the first time and it will be a memory for a lifetime. 👌🏼😁",
          date: "2:01 PM · Jun 15, 2024",
        },
        5: {
          name: "Tsvetan Valkov",
          handle: "@tsetsos",
          content:
            "Unique experience and unforgettable memory. My 10-year-old son is already looking forward to next summer. Thank you for the emotion!",
          date: "9:40 AM · Aug 5, 2024",
        },
        6: {
          name: "Doneliya Angelova",
          handle: "@doneliya",
          content:
            "Incredible emotion, great place, great professionals! This must be experienced! 😀",
          date: "4:18 PM · May 28, 2024",
        },
      },
    },
    priceData: {
      tourist: {
        divingWithVideo: "Try dive with underwater video (speedboat, instructor, full equipment)",
      },
      certified: {
        deepDive: "1 boat dive (speedboat, tanks, weights, guide)",
        nightDive: "Night dive (speedboat, tanks, weights, guide)",
        wreckDive: "Wreck dive – Mopang or Rodina",
        equipmentRental: "Full equipment rental",
      },
      cmas: {
        oneStar: "CMAS One Star Diver (P1)",
        twoStar: "CMAS Two Star Diver (P2)",
        threeStar: "CMAS Three Star Diver (P3)",
      },
    },
    pricesPage: {
      currency: "€",
      pageTitle: "Rates & packages",
      optionalExtra: "Optional add-on",
      forBeginners: "For Beginners",
      cmasCoursesTitle: "About CMAS Courses",
      cmasIntro: "Official CMAS program levels - tap a card link for the full federation syllabus.",
      cmasFullName:
        "Confédération Mondiale des Activités Subaquatiques (World Underwater Federation)",
      viewDetails: "View course details",
      certificationCardsTitle: "Your CMAS Certification Card",
      certificationCardsDescription:
        "Upon successful completion of your course, you'll receive an internationally recognized CMAS certification card. This card is your passport to dive sites worldwide and is accepted by dive centers across the globe.",
      certificationCardsAlt: "CMAS certification cards examples for all star levels",
      p1: {
        title: "P1 – One Star Diver",
        description:
          "Your gateway to the underwater world. This comprehensive 7-day course combines theory classes, pool sessions, and open water dives. Perfect for beginners—no prior experience needed.",
        features: {
          depth: "Dive to 20m depth",
          skills: "Buoyancy control, navigation, mask clearing, emergency procedures",
          certificate: "Internationally recognized CMAS* certification",
          duration: "7 days: theory, confined water & open water dives",
        },
        learnMore: "Learn more about CMAS* (One Star Diver)",
      },
      p2: {
        title: "P2 – Two Star Diver",
        description:
          "Expand your diving horizons. Advanced training in deep diving, night diving, and challenging conditions. Requires P1 certification and minimum 20 logged dives.",
        features: {
          depth: "Dive to 30m depth in varying conditions",
          skills: "Deep diving, night diving, advanced navigation & rescue techniques",
          certificate: "Internationally recognized CMAS** certification",
          duration: "5-7 days with advanced theory and specialized dives",
        },
        learnMore: "Learn more about CMAS** (Two Star Diver)",
      },
      p3: {
        title: "P3 – Three Star Diver",
        description:
          "Become a dive leader. Professional-level training in group leadership, dive planning, rescue coordination, and equipment maintenance. Step toward instructor certification.",
        features: {
          leadership: "Lead and supervise dive groups safely",
          navigation: "Advanced orientation, planning & emergency response",
          certificate: "Internationally recognized CMAS*** professional certification",
          duration: "Intensive training with leadership & rescue scenarios",
        },
        learnMore: "Learn more about CMAS*** (Three Star Diver)",
      },
    },
  },
};

export const enMetadata: LanguageMetadata = {
  name: "English",
  code: "en",
};
